import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Link as RouterLink } from "react-router-dom";
import { History, CheckCircle2, ChevronDown, ChevronRight, ArrowLeft } from "lucide-react";

function fmtMoney(n) {
  return `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

export default function PayoutHistoryPage() {
  const [payouts, setPayouts] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/payouts");
        setPayouts(data);
        if (data.length > 0) setExpandedId(data[0].payout_id);
      } catch {
        setPayouts([]);
      }
    })();
  }, []);

  const lifetimeTotal = (payouts || []).reduce((acc, p) => acc + (Number(p.affiliate_amount) || 0), 0);

  return (
    <DashboardLayout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-[#002FA7]">Payout history</div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827]">Past earnings</h1>
          <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
            Every commission you've been paid, with the per-lead snapshot for each payout.
          </p>
        </div>
        <RouterLink
          to="/dashboard/earnings"
          data-testid="payouts-back-to-current"
          className="btn-secondary inline-flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Current earnings
        </RouterLink>
      </div>

      {payouts === null ? (
        <div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" />
      ) : payouts.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Lifetime total */}
          <div className="card-flat p-6 mb-6" data-testid="payouts-lifetime-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="label-eyebrow text-[#4B5563]">Lifetime paid commissions</div>
                <div className="font-display font-black text-4xl text-[#002FA7] mt-1">{fmtMoney(lifetimeTotal)}</div>
              </div>
              <div className="text-right">
                <div className="label-eyebrow text-[#4B5563]">Number of payouts</div>
                <div className="font-display font-black text-4xl text-[#111827] mt-1">{payouts.length}</div>
              </div>
            </div>
          </div>

          {/* Per-payout list */}
          <div className="space-y-3" data-testid="payouts-list">
            {payouts.map((p) => {
              const isOpen = expandedId === p.payout_id;
              return (
                <div key={p.payout_id} className="card-flat overflow-hidden" data-testid={`payout-row-${p.payout_id}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : p.payout_id)}
                    className="w-full flex items-center justify-between gap-3 px-6 py-4 hover:bg-[#FAFAFA] text-left"
                    data-testid={`payout-toggle-${p.payout_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-[#F0FDF4] text-[#166534] flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={18} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-black text-xl text-[#111827]">{fmtMoney(p.affiliate_amount)}</div>
                        <div className="text-xs text-[#4B5563]">{fmtDateTime(p.paid_at)} · {p.affiliate_sale_count} {p.affiliate_sale_count === 1 ? "sale" : "sales"}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[#4B5563]">
                      <span className="text-xs font-bold">{isOpen ? "Hide" : "Details"}</span>
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-[#E5E7EB] bg-[#FAFAFA] px-6 py-4" data-testid={`payout-details-${p.payout_id}`}>
                      <div className="label-eyebrow text-[#4B5563] mb-3">Per-lead snapshot at payout</div>
                      {p.leads.length === 0 ? (
                        <div className="text-sm text-[#4B5563]">No per-lead breakdown available for this payout.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">
                                <th className="py-2">Lead</th>
                                <th className="py-2 text-right">Sales</th>
                                <th className="py-2 text-right">Sold to Matrix</th>
                                <th className="py-2 text-right">Your 10%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.leads.map((l) => (
                                <tr key={l.lead_id || `direct-${p.payout_id}`} className="border-t border-[#F3F4F6]">
                                  <td className="py-2">
                                    <div className="font-bold text-[#111827]">{l.lead_name}</div>
                                    {l.lead_email && <div className="text-xs text-[#9CA3AF]">{l.lead_email}</div>}
                                  </td>
                                  <td className="py-2 text-right text-[#111827]">{l.sales_count}</td>
                                  <td className="py-2 text-right font-mono text-[#111827]">{fmtMoney(l.total_amount)}</td>
                                  <td className="py-2 text-right font-mono font-bold text-[#002FA7]">{fmtMoney(l.total_commission)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t-2 border-[#E5E7EB]">
                                <td colSpan={3} className="py-3 text-right text-xs font-bold tracking-wider uppercase text-[#4B5563]">Payout total</td>
                                <td className="py-3 text-right font-display font-black text-xl text-[#166534]">{fmtMoney(p.affiliate_amount)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                      <div className="mt-3 text-[10px] text-[#9CA3AF] font-mono">Payout ID: {p.payout_id}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

function EmptyState() {
  return (
    <div className="card-flat p-12 text-center" data-testid="payouts-empty">
      <div className="mx-auto h-14 w-14 rounded-full bg-[#F3F4F6] flex items-center justify-center mb-4">
        <History size={24} className="text-[#4B5563]" />
      </div>
      <div className="font-display font-black text-2xl text-[#111827]">No payouts yet</div>
      <p className="text-sm text-[#4B5563] mt-2 max-w-md mx-auto">
        Once admin processes your first payout, the receipt will appear here with the per-lead breakdown of what you were paid for.
      </p>
    </div>
  );
}
